/**
 * Search bar component for searching cached OpenStack data.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { search, SearchResultItem } from '../api/openstack';
import { SearchIcon } from './Icons';
import { logError } from '../utils/errorLogging';
import './SearchBar.css';

export const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Debounce search requests
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Show dropdown when typing (even before results come back)
    setIsOpen(true);
    
    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await search(query.trim(), 20);
        setResults(response.results);
        // Keep dropdown open if there are results or if query is still there
        setIsOpen(true);
        setSelectedIndex(-1);
        setHighlightedIndex(-1);
      } catch (error) {
        logError('Search failed', error);
        setResults([]);
        setIsOpen(true); // Keep dropdown open to show error/no results
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Close dropdown and collapse when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!query.trim()) {
          setIsExpanded(false);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded, query]);
  
  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        // If dropdown is closed but query exists, perform search
        // This is handled by the debounced search effect
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelectResult(results[highlightedIndex]);
        } else if (results.length > 0) {
          // If no item highlighted, select first result
          handleSelectResult(results[0]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (query.trim()) {
          setQuery('');
        } else {
          setIsExpanded(false);
          inputRef.current?.blur();
        }
        break;
    }
  }, [isOpen, results, highlightedIndex, query]);

  const handleSelectResult = (result: SearchResultItem) => {
    if (result.type === 'project') {
      navigate(`/projects/${result.project_id}`);
    } else if (result.type === 'vm') {
      navigate(`/projects/${result.project_id}/vms/${result.id}`);
    }
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setIsExpanded(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleSearchButtonClick = () => {
    setIsExpanded(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setIsOpen(true);
    }
  };

  const getMatchLabel = (result: SearchResultItem): string => {
    if (!result.match_field) return '';
    
    const fieldLabels: Record<string, string> = {
      name: 'Name',
      id: 'ID',
      ip_address: 'IP Address',
      key_name: 'Key',
      created_by_user_name: 'Created By',
    };
    
    return fieldLabels[result.match_field] || result.match_field;
  };

  return (
    <div className={`search-bar ${isExpanded ? 'expanded' : ''}`} ref={searchRef}>
      {!isExpanded ? (
        <button
          className="search-bar-button"
          onClick={handleSearchButtonClick}
          aria-label="Open search"
          type="button"
        >
          <SearchIcon className="search-bar-button-icon" />
        </button>
      ) : (
        <div className="search-bar-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="search-bar-input"
            placeholder="Search projects, VMs, IPs, keys..."
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            aria-label="Search"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          />
          {isLoading && (
            <div className="search-bar-loading" aria-label="Searching...">
              <div className="search-bar-spinner"></div>
            </div>
          )}
        </div>
      )}
      {isOpen && results.length > 0 && (
        <div className="search-bar-dropdown" role="listbox">
          {results.map((result, index) => (
            <button
              key={`${result.type}-${result.id}`}
              type="button"
              className={`search-bar-result ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              onClick={() => handleSelectResult(result)}
              role="option"
              aria-selected={index === highlightedIndex}
            >
              <div className="search-bar-result-header">
                <span className={`search-bar-result-type ${result.type}`}>
                  {result.type === 'project' ? 'Project' : 'VM'}
                </span>
                {result.match_field && (
                  <span className="search-bar-result-match">
                    {getMatchLabel(result)}: {result.match_value}
                  </span>
                )}
              </div>
              <div className="search-bar-result-name">{result.name || result.id}</div>
              {result.type === 'vm' && (
                <div className="search-bar-result-project">
                  Project: {result.cloud_name || result.project_name || result.project_id}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {isOpen && !isLoading && query.trim() && results.length === 0 && (
        <div className="search-bar-dropdown">
          <div className="search-bar-no-results">No results found</div>
        </div>
      )}
    </div>
  );
};

